/**
 * carrierEvidenceCopy — the BINDING copy for the carrier-evidence decision
 * card's four presentations.
 *
 * These strings are fixed by the PRD's "The carrier-evidence card (2026-08-25
 * operator review)" section. They are BINDING: the must_pass probe fails on
 * substituted wording, and an implementer may not "improve" them. One module
 * owns them so both hosts — and the later `shared-decision-components`
 * consolidation — read the same strings.
 *
 * `{carrier}` is the ONLY interpolation. When the run reports no carrier name
 * the neutral variant renders; a name is never invented.
 */

export type EvidencePresentation =
  /** carrier supplies, no override */
  | "carrier_posts"
  /** AM branch, nothing attached */
  | "am_supplying"
  /** AM branch because the carrier does not post declarations */
  | "carrier_does_not_post"
  /** evidence is attached */
  | "attached";

export interface EvidencePresentationCopy {
  title: string;
  /** Neutral title used when the run reports no carrier name. */
  titleNeutral: string;
  body: string;
  primaryAction: string;
}

/** Exactly four entries — the probe asserts the count. */
export const CARRIER_EVIDENCE_COPY: Record<
  EvidencePresentation,
  EvidencePresentationCopy
> = {
  carrier_posts: {
    title: "{carrier} posts the declarations",
    titleNeutral: "The carrier posts the declarations",
    // The calm wait copy, carried over verbatim from the shipped
    // PendingEdocsDrawerBody so the wait branch keeps saying exactly what it
    // said before this card replaced the separate status element.
    body:
      "Waiting for the carrier to deliver the updated policy documents into " +
      "Epic. We check every day automatically, and this resumes on its own " +
      "when they arrive — there's nothing you need to do here.",
    primaryAction: "Skip the wait — I will attach evidence",
  },
  am_supplying: {
    title: "You are attaching the evidence",
    titleNeutral: "You are attaching the evidence",
    body:
      "Work the carrier portal now; Epic steps keep running. This run cannot " +
      "finish until the evidence lands.",
    primaryAction: "Attach evidence",
  },
  carrier_does_not_post: {
    title: "{carrier} does not post declarations to ora",
    titleNeutral: "This carrier does not post declarations to ora",
    body:
      "Got it. ora picks this up when it reaches the evidence step — Epic " +
      "keeps running meanwhile.",
    primaryAction: "Attach evidence",
  },
  attached: {
    title: "You are attaching the evidence",
    titleNeutral: "You are attaching the evidence",
    // NOT in the PRD's binding list: the list fixes three titles, two bodies
    // and four actions, and carries no body for the attached presentation.
    // Recorded here rather than improvised at a call site, so the next surface
    // reads the same string.
    body: "The evidence is attached. ora uses it when it reaches the evidence step.",
    primaryAction: "Attach another",
  },
};

/** The revert link. Offered only while the carrier is still capable of posting
 *  the declarations and nothing is attached. */
export const CARRIER_EVIDENCE_REVERT_LABEL = "Wait for the carrier instead";

/** The badge that marks the AM-supplies branch's evidence as required. */
export const CARRIER_EVIDENCE_REQUIRED_BADGE = "Required";

/** Announced to assistive technology when the branch becomes AM-supplies. */
export const CARRIER_EVIDENCE_REQUIRED_ANNOUNCEMENT = "Evidence now required";

/** Shown instead of the take-over branch once the carrier's documents landed. */
export const CARRIER_EVIDENCE_ALREADY_RESUMED =
  "The documents arrived, so there is nothing left to take over.";

export function resolveTitle(
  presentation: EvidencePresentation,
  carrierName: string | null,
): string {
  const copy = CARRIER_EVIDENCE_COPY[presentation];
  return carrierName
    ? copy.title.replace("{carrier}", carrierName)
    : copy.titleNeutral;
}
